import {
  type ColumnDef,
  type ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { ArrowDown, ArrowUp } from 'lucide-react'
import { memo, useEffect, useMemo, useState } from 'react'
import { TableFrontendPagination } from '@/components/shared/table-frontend-pagination'
import { Button } from '@/components/ui/button'
import { CardContent, CardFooter } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  useAuthenticatedIdUser,
  useIdCompany,
  useSuspenseQuery,
} from '@/lib/hooks'
import * as Cells from '@/routes/user-management/users-table/cells'
import { COMPANY_USERS_QUERY } from '@/routes/user-management/users-table/gql'
import type {
  CompanyUsersQuery,
  CompanyUsersQueryVariables,
} from '@/routes/user-management/users-table/gql.types.gen'
import type {
  Row,
  UserManagementService,
} from '@/routes/user-management/users-table/types'

export const UsersTable = memo(({ filterTerm }: { filterTerm: string }) => {
  const { idCompany } = useIdCompany()
  const authenticatedIdUser = useAuthenticatedIdUser()
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])

  const { data } = useSuspenseQuery<
    CompanyUsersQuery,
    CompanyUsersQueryVariables
  >(COMPANY_USERS_QUERY, { variables: { idCompany } })

  const items = useMemo(
    () =>
      [
        ...(data?.company?.pendingInvitations ?? []).map(
          (pendingInvitation) => ({
            ...pendingInvitation,
            __typename: 'CompanyUser' as const,
            user: {
              __typename: 'User' as const,
              id: pendingInvitation.id + '-pending',
              email: pendingInvitation.email,
              image: '',
              firstName: '',
              lastName: '',
              fullName: '',
              type2FA: null,
              isInvitationPending: true,
            },
          })
        ),
        ...(data?.company?.users ?? []).map((user) => ({
          ...user,
          user: {
            ...user.user,
            isInvitationPending: false,
          },
        })),
      ] as Row[],
    [data]
  )

  const services = useMemo<UserManagementService[]>(
    () => [
      ...(data?.company?.apps?.items ?? []),
      ...(data?.company?.databases?.items ?? []),
      ...(data?.company?.staticSites?.items ?? []),
      ...(data?.company?.objectStorages?.items ?? []),
      ...(data?.company?.edgeLoadBalancers?.items ?? []),
    ],
    [data]
  )

  const columns: ColumnDef<Row>[] = useMemo(
    () => [
      {
        id: 'fullName',
        accessorFn: (row) => row.user.fullName ?? row.user.email ?? '',
        size: 400,
        header: ({ column }) => (
          <Button
            className="cursor-pointer"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Name{' '}
            {column.getIsSorted() === 'asc' ? (
              <ArrowUp className="ml-2 size-4" />
            ) : column.getIsSorted() === 'desc' ? (
              <ArrowDown className="ml-2 size-4" />
            ) : null}
          </Button>
        ),
        cell: Cells.NameCell,
      },
      {
        accessorKey: 'email',
        size: 200,
        header: 'Email',
        cell: Cells.EmailCell,
      },
      {
        accessorKey: 'twoFa',
        size: 200,
        header: '2FA',
        cell: Cells.TwoFaCell,
      },
      {
        accessorKey: 'role',
        size: 200,
        header: 'Role',
        cell: Cells.RoleCell,
      },
      {
        id: 'actions',
        enableHiding: false,
        cell: Cells.ActionsCell,
      },
    ],
    []
  )

  const table = useReactTable({
    data: items,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
    },
    meta: {
      authenticatedIdUser,
      projects: data?.company?.projects?.items ?? [],
      services,
      showProjectAccessTooltip: true,
    },
  })

  useEffect(() => {
    table.getColumn('fullName')?.setFilterValue(filterTerm ?? '')
  }, [table, filterTerm])

  return (
    <>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow
                  key={headerGroup.id}
                  className="bg-muted/50 hover:bg-muted/50"
                >
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length
                ? table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id} className="cursor-pointer">
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          className="p-0 m-0"
                          key={cell.id}
                          style={{ maxWidth: cell.column.getSize() }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : null}
              {!table.getRowModel().rows?.length ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No users found.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </div>
      </CardContent>
      <CardFooter>
        <TableFrontendPagination table={table} />
      </CardFooter>
    </>
  )
})
